import { memo } from 'react'
import { useStore } from '../core/store'
import type { ReasonNode } from '../core/types'
import { canvasTookTouch, handToCanvas } from './labelTouch'
import { seedTitle } from './seedCaption'

/**
 * The seed's caption: a title of the prompt's
 * opening words, bare on the field at the centre of the graph, and a "Full
 * prompt" control under it that opens the prompt sheet (`ui/PromptSheet.tsx`)
 * — always, so the door to the prompt is in one place whatever its length. Two buttons side by side in a box,
 * not one inside the other, because a button cannot hold a button.
 *
 * Its own component rather than a branch of `Label`, because it is a different
 * shape: every other caption's text is the node's name, and the seed's name is
 * derived from the prompt (`seedTitle`, `seedCaption.ts`). One button, so the
 * title is one target. A click on it selects the seed and opens the thread,
 * as every caption's does — and the seed's card there is titled with the whole
 * prompt (D90), so the full question is one click from the short one. A
 * double-click focuses, as everywhere. The frame loop owns `transform`, `scale`, `opacity` and
 * `visibility` on the BOX; nothing here writes those.
 *
 * No `title` attribute. The thread is the way to read the prompt, and a
 * tooltip would be the wall of text this caption exists to remove, hung off
 * the pointer instead.
 */

export type Register = (id: string, el: HTMLElement | null) => void

export const SeedLabel = memo(function SeedLabel({
  n,
  selected,
  guide,
  register,
}: {
  n: ReasonNode
  selected: boolean
  guide: boolean
  register: Register
}) {
  const { head, tail } = seedTitle(n.label)
  return (
    <div
      ref={(el) => register(n.id, el)}
      className="nlabel"
      aria-hidden
      data-root="true"
      data-sel={selected || undefined}
      data-guide={guide || undefined}
      onPointerEnter={() => useStore.getState().hover(n.id)}
      onPointerLeave={() => useStore.getState().hover(null)}
      // The seed's caption sits on the middle of the graph, under most pinches,
      // and at a zoom it covers the captions around it: a finger anywhere on it
      // is the canvas's, and its tap still selects the seed (`labelTouch.ts`,
      // D340). The box, not only the words, because the box takes the press too.
      onPointerDown={(e) => {
        if ((e.target as Element).closest('.nlabel-expand')) return
        if (handToCanvas(e.nativeEvent, n.id)) e.stopPropagation()
      }}
    >
      <button
        className="nlabel-seed"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation()
          // A finger's tap here was the canvas's to answer (`labelTouch.ts`).
          if (canvasTookTouch(e.nativeEvent)) return
          // No ⌥-click fold here: `toggleCollapse` refuses the seed — folding
          // it would fold the app — so the modifier falls through to a select.
          useStore.getState().select(n.id)
          useStore.getState().setRail(true)
        }}
        onDoubleClick={(e) => {
          e.stopPropagation()
          const st = useStore.getState()
          st.setFocus(st.focusId === n.id ? null : n.id)
        }}
      >
        <span className="nlabel-head">{head}</span>
        {tail && <span className="nlabel-tail">{tail}</span>}
      </button>
      <button
        className="nlabel-expand"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation()
          useStore.getState().setPromptOpen(true)
        }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <path
            d="M6 1h3v3M9 1 5.6 4.4M4 9H1V6M1 9l3.4-3.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Full prompt
      </button>
    </div>
  )
})
